export const isJSON = str => {
	try {
		return JSON.parse(str) && !!str;
	} catch (e) {
		return false;
	}
};

/**
 * Checks if a JWT access token exists in local storage.
 * 
 * @returns {boolean} true if the token exists, false otherwise.
 */
export const getAccessToken = () => {
	if (localStorage.getItem('jwt_access_token')) {
		return true;
	}
	return false;
}

/**
 * Generates a globally unique identifier (GUID) as a string.
 * This function creates a GUID by concatenating two randomly generated
 * hexadecimal segments.
 * 
 * @returns {string} A string representing the GUID.
 */
export function generateGUID() {
	function S4() {
		return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
	} return S4() + S4();
}

/**
 * Compare two dates and check if they are within a certain number of minutes of each other.
 * This function first checks if the two dates are the same day, and if so, calculates the
 * absolute difference in milliseconds and converts it to minutes. If the difference is
 * less than the specified number of minutes, the function returns true. Otherwise, it
 * returns false.
 * 
 * @param {string|Date} date1 The first date to compare.
 * @param {string|Date} date2 The second date to compare.
 * @param {number} [minutes=30] The number of minutes to check for.
 * @returns {boolean} True if the dates are within the specified number of minutes of each
 *  other, false otherwise.
 */
export const compareDatesByMinutes = (date1, date2, minutes = 30) => {
	// Check if either date1 or date2 is empty
	if (!date1 || !date2) {
		return false;
    }
	// Convert both dates to timestamps
	let timestamp1 = new Date(date1).getTime();
	let timestamp2 = new Date(date2).getTime();

	// If dates are different, return false
	let datePart1 = new Date(date1).toDateString();
	let datePart2 = new Date(date2).toDateString();
	if (datePart1 !== datePart2) {
		return false;
	}

	// Calculate the absolute difference in milliseconds
	let diffMilliseconds = Math.abs(timestamp1 - timestamp2);

	// Convert milliseconds to minutes
	let diffMinutes = diffMilliseconds / (1000 * 60);

	// Check if the difference is less than minutes(default = 30 minutes) minutes
	return diffMinutes < minutes;
}

/**
 * Checks if a given object has any filter values selected.
 * 
 * @param {Object} data The object to check.
 * @returns {boolean} true if any filter values are selected, false otherwise.
 */
export const isClearShow = (data) => {
	return Object.values(data).some(value => {
		if (Array.isArray(value)) {
			return value.length > 0;
		}
		return value !== null;
	});
};

function getUserStorage(userId) {
	if (!userId) return {};

	const raw = localStorage.getItem(userId);
	try {
		return raw ? JSON.parse(raw) : {};
	} catch (e) {
		console.error(`Failed to parse localStorage for user ${userId}:`, e);
		return {};
	}
}

export function setStructuredLocalStorage(userId, appId, pageUrl, data = {}) {
	if (!userId || !appId || !pageUrl) {
		console.warn('Missing key(s) for structured localStorage');
		return;
	}

	const userStorage = getUserStorage(userId);

	userStorage[appId] = userStorage[appId] || {};
	userStorage[appId][pageUrl] = {
		...(userStorage[appId][pageUrl] || {}),
		...data
	};
	localStorage.setItem(userId, JSON.stringify(userStorage));
}

export function getStructuredLocalStorage(userId = null, appId = null, pageUrl = null, dataKey = null) {
	if (!userId) return null; // Return null if no userId is provided
	let output = getUserStorage(userId); // Get the full user-specific object from localStorage
	if (appId) output = output[appId] || {}; // If appId is provided, drill down into that app's data;
	if (pageUrl) output = output[pageUrl] || {}; // If pageUrl is provided, further drill down into page-specific data;
	if (dataKey) output = output[dataKey] || {}; // If dataKey is provided, extract that specific piece of data;
	return output;  // Return the final resolved object (or {} if not found)
}

/**
 * Gets orgId from multiple fallback sources.
 * This is useful for SSO/SAML login scenarios where orgId might not be available in Redux state.
 * 
 * @param {Object} props - Component props containing user, org, and login Redux states
 * @returns {string|null} The orgId if found, null otherwise
 * 
 * Fallback order:
 * 1. props.user?.orgId - Redux user state
 * 2. props.org?.getOrgDetails?.org?._id - Redux org state
 * 3. props.login?.emailCheckData?.orgId - Redux login state (SSO email check)
 * 4. jwtuserData from localStorage
 * 5. userEmailReport from localStorage (base64 decoded)
 * 6. ssoConfig from localStorage (base64 decoded)
 */
export const getOrgId = (props = {}) => {
	// Primary: from user Redux state
	if (props.user?.orgId) {
		return props.user.orgId;
	}
	
	// Fallback 1: from org Redux state
	if (props.org?.getOrgDetails?.org?._id) {
		return props.org.getOrgDetails.org._id;
	}
	
	// Fallback 2: from login emailCheckData Redux state
	if (props.login?.emailCheckData?.orgId) {
		return props.login.emailCheckData.orgId;
	}
	
	// Fallback 3: from jwtuserData in localStorage
	try {
		const rawJwtUserData = localStorage.getItem('jwtuserData');
		if (rawJwtUserData) {
			const jwtUserData = JSON.parse(rawJwtUserData);
			if (jwtUserData?.orgId) {
				return jwtUserData.orgId;
			}
		}
	} catch (e) {
		console.log('Error parsing jwtuserData:', e);
	}
	
	// Fallback 4: from userEmailReport in localStorage
	try {
		const rawUserEmailReport = localStorage.getItem('userEmailReport');
		if (rawUserEmailReport) {
			const emailCheckData = JSON.parse(window.atob(rawUserEmailReport));
			if (emailCheckData?.orgId) {
				return emailCheckData.orgId;
			}
		}
	} catch (e) {
		console.log('Error parsing userEmailReport:', e);
	}
	
	// Fallback 5: from ssoConfig in localStorage
	try {
		const rawSsoConfig = localStorage.getItem('ssoConfig');
		if (rawSsoConfig) {
			const ssoConfig = JSON.parse(window.atob(rawSsoConfig));
			if (ssoConfig?.orgId) {
				return ssoConfig.orgId;
			}
		}
	} catch (e) {
		console.log('Error parsing ssoConfig:', e);
	}
	
	return null;
};